// Thin TanStack Query wrapper used by every report page.
//
// Centralising it keeps cache keys tenant-scoped, enforces a 30s stale time
// (reports are relatively expensive to compute server-side), and gives us a
// single place to tweak retry/refetch policy later.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { storeTimezone } from '@/lib/store-clock';
import { resolveTimeRange } from './date-range';
import type { ReportFilterValue } from './types';
import { useMemo } from 'react';
import type { TimeRangeValue } from '@bbpos/ui';

interface UseReportQueryOptions<T> {
  key: string;
  filter: ReportFilterValue;
  extraKey?: readonly (string | number | boolean | undefined)[];
  queryFn: (filter: ReportFilterValue) => Promise<T>;
  enabled?: boolean;
}

export function useReportQuery<T>({
  key,
  filter,
  extraKey = [],
  queryFn,
  enabled = true,
}: UseReportQueryOptions<T>): UseQueryResult<T> {
  const tenantId = useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');

  return useQuery({
    queryKey: ['reports', key, tenantId, filter.range.from, filter.range.to, filter.locationId ?? 'all', ...extraKey],
    queryFn: () => queryFn(filter),
    staleTime: 30_000,
    enabled,
    retry: (failureCount, error) => {
      // Don't spin on auth / permission errors
      const status = (error as { statusCode?: number }).statusCode;
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Builds a memoised ReportFilterValue from the TimeRangeValue + selected
 * location. Every report page uses this, so it lives in one spot.
 */
export function useReportFilter(
  timeRange: TimeRangeValue,
  locationId: string | undefined,
): ReportFilterValue {
  // T20: presets count from the store's day — the filtered store's, else the business's.
  const profile = useAuthStore((s) => s.profile);
  const timeZone = storeTimezone(profile, locationId);
  return useMemo(
    () => ({ range: resolveTimeRange(timeRange, timeZone), locationId }),
    [timeRange, timeZone, locationId],
  );
}
